import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import api from '../../services/api'
import { Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import './AgendaPage.css'

export default function AgendaPage() {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const calendarRef = useRef(null)
    const [events, setEvents] = useState([])
    const [loading, setLoading] = useState(false)

    const fetchEvents = async (start, end) => {
        setLoading(true)
        try {
            const response = await api.get('/rendezvous/agenda', {
                params: {
                    start: start.toISOString(),
                    end: end.toISOString()
                }
            })

            const formattedEvents = response.data.data.events.map(event => ({
                id: event.id,
                title: event.title,
                start: event.start,
                end: event.end,
                backgroundColor: getStateColor(event.status),
                borderColor: getStateColor(event.status),
                extendedProps: {
                    status: event.status,
                    phone: event.phone,
                    agent: event.agent,
                    conseiller: event.conseiller,
                    address: event.address
                }
            }))

            setEvents(formattedEvents)
        } catch (error) {
            console.error('Error fetching events:', error)
            // Don't show error toast for empty calendar
            if (error.response?.status !== 404) {
                toast.error(t('common.error'))
            }
            setEvents([])
        } finally {
            setLoading(false)
        }
    }

    const getStateColor = (state) => {
        const colors = {
            PLANIFIE: '#3b82f6',
            QUALITE_DONE: '#8b5cf6',
            DISPATCHED: '#f59e0b',
            CONSEILLER_DONE: '#10b981',
            ANNULE: '#ef4444'
        }
        return colors[state] || '#64748b'
    }

    const handleDatesSet = (dateInfo) => {
        fetchEvents(dateInfo.start, dateInfo.end)
    }

    const handleEventClick = (clickInfo) => {
        navigate(`/rendezvous/${clickInfo.event.id}`)
    }

    const handleDateClick = (dateInfo) => {
        // Navigate to add page with pre-filled date
        navigate(`/rendezvous/add?date=${dateInfo.dateStr}`)
    }

    return (
        <div className="agenda-page">
            <div className="page-header">
                <h1 className="page-title">{t('agenda.title')}</h1>
            </div>

            <div className="card agenda-card">
                {loading && (
                    <div className="agenda-loading-overlay">
                        <Loader2 size={32} className="spin" />
                    </div>
                )}
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView="timeGridWeek"
                    locale={i18n.language}
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay'
                    }}
                    buttonText={{
                        today: t('agenda.today'),
                        month: t('agenda.month'),
                        week: t('agenda.week'),
                        day: t('agenda.day')
                    }}
                    events={events}
                    datesSet={handleDatesSet}
                    eventClick={handleEventClick}
                    dateClick={handleDateClick}
                    slotMinTime="08:00:00"
                    slotMaxTime="19:00:00"
                    allDaySlot={false}
                    nowIndicator={true}
                    editable={false}
                    selectable={true}
                    height="auto"
                    eventTimeFormat={{
                        hour: '2-digit',
                        minute: '2-digit',
                        meridiem: false
                    }}
                    eventDisplay="block"
                    eventContent={(arg) => (
                        <div className="fc-event-content">
                            <span className="fc-event-time">{arg.timeText}</span>
                            <span className="fc-event-title">{arg.event.title}</span>
                        </div>
                    )}
                />
            </div>

            {/* Legend */}
            <div className="card agenda-legend">
                <div className="legend-title">{t('rendezvous.etat')}</div>
                <div className="legend-items">
                    <div className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: '#3b82f6' }}></span>
                        <span>{t('rendezvous.states.PLANIFIE')}</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: '#8b5cf6' }}></span>
                        <span>{t('rendezvous.states.QUALITE_DONE')}</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: '#f59e0b' }}></span>
                        <span>{t('rendezvous.states.DISPATCHED')}</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: '#10b981' }}></span>
                        <span>{t('rendezvous.states.CONSEILLER_DONE')}</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: '#ef4444' }}></span>
                        <span>{t('rendezvous.states.ANNULE')}</span>
                    </div>
                </div>
            </div>
        </div>
    )
}
